import { motion } from "framer-motion";
import { Outlet } from "react-router-dom";
import { AuthBackdrop } from "@/components/auth/auth-backdrop";
import { Brand } from "@/components/layout/brand";

/**
 * Split shell: the network diagram carries the product's story on the left, the
 * form gets an uncluttered column on the right. Below `lg` the diagram becomes a
 * short banner rather than disappearing entirely.
 */
export function AuthLayout() {
  return (
    <div className="grid min-h-dvh grid-cols-1 lg:grid-cols-[1.05fr_minmax(420px,0.95fr)]">
      <section className="relative hidden overflow-hidden border-r bg-muted/40 lg:flex lg:flex-col lg:justify-between lg:p-10">
        <AuthBackdrop />

        <div className="relative">
          <Brand />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.15, ease: [0.2, 0.8, 0.2, 1] }}
          className="relative max-w-md"
        >
          {/* The city runs behind the copy, so the text gets its own scrim.
              DOM order does the layering — a negative z-index would drop it
              behind the backdrop instead of on top of it. The falloff is spread
              over four stops: a two-stop ellipse reads as a dark smudge on the
              dark theme, where the scrim colour is the thing being faded in. */}
          <div className="pointer-events-none absolute -inset-x-12 -inset-y-10 bg-[radial-gradient(ellipse_at_center,var(--background)_28%,color-mix(in_oklch,var(--background)_78%,transparent)_50%,color-mix(in_oklch,var(--background)_34%,transparent)_70%,transparent_88%)]" />

          <div className="relative space-y-4">
            <p className="label-micro">Fleet telemetry, live</p>
            <h2 className="text-[26px] leading-[1.15] font-semibold tracking-tight text-balance">
              Every vehicle, every ping, on one map.
            </h2>
            <p className="text-sm leading-relaxed text-muted-foreground">
              Positions stream in over a socket the moment a device reports.
              Geofences, trips and alerts are evaluated off the ingest path, so
              the map never waits on the work behind it.
            </p>

            <dl className="grid grid-cols-3 gap-3 pt-2">
              {[
                { label: "Update latency", value: "<1s" },
                { label: "Fence checks", value: "Per ping" },
                { label: "History", value: "90 days" },
              ].map((item) => (
                <div
                  key={item.label}
                  className="rounded-xl bg-card/70 px-3 py-2.5 shadow-card backdrop-blur-sm"
                >
                  <dd className="tnum text-base font-semibold">
                    {item.value}
                  </dd>
                  <dt className="label-micro pt-0.5 text-[9px]">
                    {item.label}
                  </dt>
                </div>
              ))}
            </dl>
          </div>
        </motion.div>

        <p className="relative text-[11px] text-muted-foreground">
          © {new Date().getFullYear()} OLE-FLEET · Fleet Operations Console
        </p>
      </section>

      <section className="relative flex items-center justify-center px-6 py-10">
        <div className="absolute top-6 left-6 lg:hidden">
          <Brand />
        </div>
        <Outlet />
      </section>
    </div>
  );
}
